import InlineScript from '../components/InlineScript';
import { FormEvent, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { pageUrl } from '../config/site';
import {
  BEFORE_AFTER,
  INTERIOR_PAINTING_SHOTS,
} from '../constants/projectGallery';
import Navigation from '../components/navigation';
import Footer from '../components/footer';
import { submitToMailer } from '../lib/submitToMailer';
import './services.css';

export default function Services() {
  const [modalMail, setModalMail] = useState<{
    status: 'idle' | 'sending' | 'ok' | 'err';
    message: string;
  }>({ status: 'idle', message: '' });

  async function handleServicesModalSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setModalMail({ status: 'sending', message: '' });
    const form = e.currentTarget;
    const fd = new FormData(form);
    const result = await submitToMailer({
      form: 'contact',
      name: String(fd.get('name') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      email: String(fd.get('email') ?? ''),
      service: String(fd.get('service') ?? ''),
      message: String(fd.get('message') ?? ''),
      contactTime: 'anytime',
      freeEstimate: false,
      fileCount: 0,
    });
    if (result.ok) {
      setModalMail({
        status: 'ok',
        message: 'Thanks — we received your request and will reply soon.',
      });
      form.reset();
      const dlg = document.getElementById(
        'contactModal',
      ) as HTMLDialogElement | null;
      dlg?.close();
    } else {
      setModalMail({ status: 'err', message: result.error });
    }
  }

  return (
    <div className="services-container1">
      <Helmet>
        <title>Services | Shiny Touch Painting &amp; Renovation</title>
        <meta
          property="og:title"
          content="Services | Shiny Touch Painting &amp; Renovation"
        />
        <link rel="canonical" href={pageUrl('/services')} />
        <meta property="og:url" content={pageUrl('/services')} />
      </Helmet>
      <Navigation></Navigation>
      <section id="hero" className="hero-overview">
        <div className="hero-media-container">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.pexels.com/videos/6474138/pictures/preview-0.jpeg"
            src="https://videos.pexels.com/video-files/6474138/6474138-hd_720_1280_25fps.mp4"
            className="hero-video"
          ></video>
          <div className="services-hero-overlay"></div>
        </div>
        <div className="services-hero-content-wrapper">
          <div className="hero-content-block">
            <div className="hero-brand-badge">
              <span className="hero-badge-text">
                Burnaby&apos;s Most Reliable Team
              </span>
            </div>
            <h1 className="services-hero-title hero-title">
              Shiny Touch Painting &amp; Renovation Ltd
            </h1>
            <p className="services-hero-subtitle hero-subtitle">
              Expert Interior &amp; Exterior Painting, Full Renovations, and
              Strata Property Maintenance. Professional craftsmanship that
              transforms your space with a reliable, affordable touch.
            </p>
            <div className="services-hero-cta-group">
              <a href="#quote">
                <div className="btn btn-primary btn-lg">
                  <span>Request a Free Quote</span>
                </div>
              </a>
              <a href="#services">
                <div className="btn btn-lg btn-outline">
                  <span>Explore Services</span>
                </div>
              </a>
            </div>
            <div className="hero-trust-indicators">
              <div className="services-trust-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
                <span>Strata Compliant</span>
              </div>
              <div className="services-trust-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
                <span>Licensed &amp; Insured</span>
              </div>
              <div className="services-trust-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
                <span>Free Estimates</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="services" className="service-panels">
        <div className="service-container">
          <div className="service-header">
            <h2 className="section-title">Our Specialized Services</h2>
            <p className="section-content">
              Comprehensive solutions tailored to homeowners and strata managers
              in Lower Mainland and the surrounding area.
            </p>
          </div>
          <div className="services-service-grid">
            <article
              id="service-interior-painting"
              className="services-service-card"
            >
              <div className="service-image-box">
                <img
                  src={INTERIOR_PAINTING_SHOTS.kitchenRenovation}
                  alt="Professional interior painting in progress — kitchen and living area"
                  loading="lazy"
                />
              </div>
              <div className="service-info">
                <h3 className="section-subtitle">Interior Painting</h3>
                <p className="section-content">
                  Full-service interior painting for homes and businesses. We
                  use premium low-VOC paints, careful masking, and thorough
                  surface prep for a durable, clean finish.
                </p>
                <ul className="service-details">
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Precision edging &amp; surface prep
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Multi-surface coatings (walls, trim, ceilings)
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Typical interior timelines: 3–7 days
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </article>
            <article
              id="service-exterior-painting"
              className="services-service-card"
            >
              <div className="service-image-box">
                <img
                  src={BEFORE_AFTER.project0.after}
                  alt="Exterior painting — finished siding and trim"
                  loading="lazy"
                />
              </div>
              <div className="service-info">
                <h3 className="section-subtitle">Exterior Painting</h3>
                <p className="section-content">
                  Weather-resistant coatings built for BC&apos;s rain and sun.
                  We protect siding, trim, decks, and fences with products
                  suited to your substrate and exposure.
                </p>
                <ul className="service-details">
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Pressure washing &amp; substrate repair
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      UV-stable and moisture-resistant systems
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Safe site setup &amp; thorough clean-up
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </article>
            <article id="service-renovation" className="services-service-card">
              <div className="service-image-box">
                <img
                  src="https://images.pexels.com/photos/5493661/pexels-photo-5493661.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1500"
                  alt="Full Renovation"
                  loading="lazy"
                />
              </div>
              <div className="service-info">
                <h3 className="section-subtitle">Full Renovation</h3>
                <p className="section-content">
                  From kitchen remodels to complete home overhauls. We manage
                  the entire process, ensuring structural integrity and modern
                  aesthetic finishes.
                </p>
                <ul className="service-details">
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Kitchen &amp; Bathroom experts
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Flooring &amp; Cabinetry
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Custom project timelines
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </article>
            <article id="service-strata" className="services-service-card">
              <div className="service-image-box">
                <img
                  src="https://images.pexels.com/photos/32115287/pexels-photo-32115287.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1500"
                  alt="Strata Property Maintenance"
                  loading="lazy"
                />
              </div>
              <div className="service-info">
                <h3 className="section-subtitle">
                  Strata Property Maintenance
                </h3>
                <p className="section-content">
                  Reliable maintenance for multi-unit complexes. We work closely
                  with strata councils to provide scheduled painting and repair
                  services.
                </p>
                <ul className="service-details">
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      High-traffic durability
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Minimal resident disruption
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                  <li>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>
                      {' '}
                      Ongoing service plans
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ',
                        }}
                      />
                    </span>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="services-process-timeline">
        <div className="timeline-container">
          <h2 className="section-title">Our 4-Step Process</h2>
          <div className="services-timeline-wrapper">
            <div className="services-timeline-track">
              <div className="services-timeline-step">
                <div className="services-step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"></path>
                    <path d="m22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7m14 12l2 2l4-4"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h4 className="section-subtitle">1. Free Estimate</h4>
                  <p className="section-content">
                    On-site assessment and detailed quote within 24 hours.
                  </p>
                </div>
              </div>
              <div className="services-timeline-step">
                <div className="services-step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 2v2m4-2v4m3-4a1 1 0 0 1 1 1v9H6V3a1 1 0 0 1 1-1zM6 12a1 1 0 0 0-1 1v1a2 2 0 0 0 2 2h2a1 1 0 0 1 1 1v2.9a2 2 0 1 0 4 0V17a1 1 0 0 1 1-1h2a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h4 className="section-subtitle">2. Preparation</h4>
                  <p className="section-content">
                    Masking, sanding, and surface cleaning for lasting results.
                  </p>
                </div>
              </div>
              <div className="services-timeline-step">
                <div className="services-step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <g
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    >
                      <rect width="16" height="6" x="2" y="2" rx="2"></rect>
                      <path d="M10 16v-2a2 2 0 0 1 2-2h8a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path>
                      <rect width="4" height="6" x="8" y="16" rx="1"></rect>
                    </g>
                  </svg>
                </div>
                <div className="step-content">
                  <h4 className="section-subtitle">3. Execution</h4>
                  <p className="section-content">
                    Expert application by our professional painting team.
                  </p>
                </div>
              </div>
              <div className="services-timeline-step">
                <div className="services-step-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77a4 4 0 0 1 6.74 0a4 4 0 0 1 4.78 4.78a4 4 0 0 1 0 6.74a4 4 0 0 1-4.77 4.78a4 4 0 0 1-6.75 0a4 4 0 0 1-4.78-4.77a4 4 0 0 1 0-6.76"></path>
                    <path d="m9 12l2 2l4-4"></path>
                  </svg>
                </div>
                <div className="step-content">
                  <h4 className="section-subtitle">4. Inspection</h4>
                  <p className="section-content">
                    Final walkthrough and handover to ensure satisfaction.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="showcase-grid">
        <div className="showcase-item">
          <div className="showcase-media">
            <img
              src={BEFORE_AFTER.project0.before}
              alt="Before — interior project"
              className="before"
            />
            <div className="showcase-label">
              <span>Before</span>
            </div>
          </div>
          <div className="showcase-content">
            <h3 className="section-subtitle">Interior Revitalization</h3>
            <p className="section-content">
              Complete color transformation for a Burnaby residence.
            </p>
          </div>
        </div>
        <div className="showcase-item">
          <div className="showcase-media">
            <img
              src={BEFORE_AFTER.project0.after}
              alt="After — interior project"
              className="after"
            />
            <div className="showcase-label after-label">
              <span>After</span>
            </div>
          </div>
          <div className="showcase-content">
            <h3 className="section-subtitle">Modern Finish</h3>
            <p className="section-content">
              Premium Eggshell finish with custom accent walls.
            </p>
          </div>
        </div>
        <div className="showcase-item">
          <div className="showcase-media">
            <img
              src={BEFORE_AFTER.project1.before}
              alt="Before — renovation project"
              className="before"
            />
            <div className="showcase-label">
              <span>Before</span>
            </div>
          </div>
          <div className="showcase-content">
            <h3 className="section-subtitle">Full Renovation Prep</h3>
            <p className="section-content">
              Stripping down old fixtures for a new beginning.
            </p>
          </div>
        </div>
        <div className="showcase-item">
          <div className="showcase-media">
            <img
              src={BEFORE_AFTER.project1.after}
              alt="After — renovation project"
              className="after"
            />
            <div className="showcase-label after-label">
              <span>After</span>
            </div>
          </div>
          <div className="showcase-content">
            <h3 className="section-subtitle">Completed Transformation</h3>
            <p className="section-content">
              Contemporary open-concept living space.
            </p>
          </div>
        </div>
      </section>
      <section className="why-choose-us">
        <div className="services-features-container">
          <div className="features-header">
            <h2 className="section-title">The Shiny Touch Advantage</h2>
            <p className="section-content">
              Professionalism you can trust, quality you can see.
            </p>
          </div>
          <div className="services-features-grid">
            <div className="feature-card">
              <div className="services-feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2zM5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1a6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Reliable &amp; Professional</h3>
              <p className="section-content">
                We show up on time, maintain a clean workspace, and respect your
                property throughout the project.
              </p>
            </div>
            <div className="feature-card">
              <div className="services-feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Affordable Excellence</h3>
              <p className="section-content">
                Premium results without the premium price tag. We offer
                competitive pricing for high-end craftsmanship.
              </p>
            </div>
            <div className="feature-card">
              <div className="services-feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                  <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Strata Compliance</h3>
              <p className="section-content">
                Fully compliant with strata bylaws and insurance requirements.
                Expert at navigating multi-unit complex logistics.
              </p>
            </div>
            <div className="feature-card">
              <div className="services-feature-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </svg>
              </div>
              <h3 className="section-subtitle">Warrantied Work</h3>
              <p className="section-content">
                Every project is backed by our satisfaction guarantee and
                comprehensive workmanship warranty.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="services-testimonials-rail">
        <div className="rail-header">
          <h2 className="section-title">What Our Clients Say</h2>
        </div>
        <div className="testimonial-track">
          <div className="services-testimonial-card">
            <div className="services-quote-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V11H16.017C14.9124 11 14.017 10.1046 14.017 9V5C14.017 3.89543 14.9124 3 16.017 3H20.017C21.1216 3 22.017 3.89543 22.017 5V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM2.01697 21L2.01697 18C2.01697 16.8954 2.9124 16 4.01697 16H7.01697C7.56925 16 8.01697 15.5523 8.01697 15V11H4.01697C2.9124 11 2.01697 10.1046 2.01697 9V5C2.01697 3.89543 2.9124 3 4.01697 3H8.01697C9.12154 3 10.017 3.89543 10.017 5V15C10.017 18.3137 7.33068 21 4.01697 21H2.01697Z"></path>
              </svg>
            </div>
            <p className="section-content">
              &quot;Shiny Touch transformed our strata building&apos;s exterior.
              Their communication was flawless, and the project finished ahead
              of schedule. Highly recommend!&quot;
            </p>
            <div className="client-info">
              <span className="client-name">Sarah Jenkins</span>
              <span className="client-role">Strata Manager, Burnaby</span>
            </div>
          </div>
          <div className="services-testimonial-card">
            <div className="services-quote-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V11H16.017C14.9124 11 14.017 10.1046 14.017 9V5C14.017 3.89543 14.9124 3 16.017 3H20.017C21.1216 3 22.017 3.89543 22.017 5V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM2.01697 21L2.01697 18C2.01697 16.8954 2.9124 16 4.01697 16H7.01697C7.56925 16 8.01697 15.5523 8.01697 15V11H4.01697C2.9124 11 2.01697 10.1046 2.01697 9V5C2.01697 3.89543 2.9124 3 4.01697 3H8.01697C9.12154 3 10.017 3.89543 10.017 5V15C10.017 18.3137 7.33068 21 4.01697 21H2.01697Z"></path>
              </svg>
            </div>
            <p className="section-content">
              &quot;The team was incredibly professional during our kitchen
              renovation. Affordable, reliable, and the quality of the painting
              is second to none.&quot;
            </p>
            <div className="client-info">
              <span className="client-name">David Wong</span>
              <span className="client-role">Homeowner</span>
            </div>
          </div>
          <div className="services-testimonial-card">
            <div className="services-quote-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V11H16.017C14.9124 11 14.017 10.1046 14.017 9V5C14.017 3.89543 14.9124 3 16.017 3H20.017C21.1216 3 22.017 3.89543 22.017 5V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM2.01697 21L2.01697 18C2.01697 16.8954 2.9124 16 4.01697 16H7.01697C7.56925 16 8.01697 15.5523 8.01697 15V11H4.01697C2.9124 11 2.01697 10.1046 2.01697 9V5C2.01697 3.89543 2.9124 3 4.01697 3H8.01697C9.12154 3 10.017 3.89543 10.017 5V15C10.017 18.3137 7.33068 21 4.01697 21H2.01697Z"></path>
              </svg>
            </div>
            <p className="section-content">
              &quot;Reliable and tidy! They painted our entire exterior and it
              looks brand new. Best painters in Burnaby by far.&quot;
            </p>
            <div className="client-info">
              <span className="client-name">Mark Thompson</span>
              <span className="client-role">Property Manager</span>
            </div>
          </div>
        </div>
      </section>
      <section className="pricing-packages">
        <div className="pricing-container">
          <div className="pricing-header">
            <h2 className="section-title">Service Packages</h2>
            <p className="section-content">
              Transparent baseline pricing to help you plan your next project.
            </p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3 className="section-subtitle">Room Refresh</h3>
              <div className="price-value">
                <span>Starting at $399</span>
              </div>
              <p className="section-content">
                Ideal for single bedrooms or living areas. Includes basic prep
                and 2 coats of premium paint.
              </p>
              <ul className="pricing-list">
                <li>
                  <span>Walls &amp; Trim included</span>
                </li>
                <li>
                  <span>Minor hole patching</span>
                </li>
                <li>
                  <span>1-Day Completion</span>
                </li>
              </ul>
            </div>
            <div className="pricing-card featured">
              <div className="card-tag">
                <span>Most Popular</span>
              </div>
              <h3 className="section-subtitle">Full Interior</h3>
              <div className="price-value">
                <span>Custom Quote</span>
              </div>
              <p className="section-content">
                Comprehensive painting for your entire home. Includes ceilings,
                baseboards, and doors.
              </p>
              <ul className="pricing-list">
                <li>
                  <span>Color consultation</span>
                </li>
                <li>
                  <span>Detailed prep work</span>
                </li>
                <li>
                  <span>Multi-room discount</span>
                </li>
              </ul>
            </div>
            <div className="pricing-card">
              <h3 className="section-subtitle">Strata Basic</h3>
              <div className="price-value">
                <span>Contract Pricing</span>
              </div>
              <p className="section-content">
                Regular maintenance for common areas, hallways, and lobbies.
                Tailored to strata budgets.
              </p>
              <ul className="pricing-list">
                <li>
                  <span>Scheduled touch-ups</span>
                </li>
                <li>
                  <span>High-durability paint</span>
                </li>
                <li>
                  <span>Compliance reporting</span>
                </li>
              </ul>
            </div>
            <div className="pricing-card">
              <h3 className="section-subtitle">Full Renovation</h3>
              <div className="price-value">
                <span>Project Based</span>
              </div>
              <p className="section-content">
                Complete remodeling packages including demolition, installation,
                and final finishing.
              </p>
              <ul className="pricing-list">
                <li>
                  <span>Fixed-fee estimates</span>
                </li>
                <li>
                  <span>Project management</span>
                </li>
                <li>
                  <span>Quality guarantee</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="pricing-note">
            Note: Prices are estimates. Please request a custom free quote for
            accurate pricing.
          </p>
        </div>
      </section>
      <section id="quote" className="services-quote-cta">
        <div className="cta-container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="section-title">Ready for a Transformation?</h2>
              <p className="section-content">
                Request your free, no-obligation estimate today. We proudly
                serve Lower Mainland and the surrounding area.
              </p>
              <div className="cta-info-grid">
                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>99632 Market Crossing PO, Burnaby, BC</span>
                </div>
                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>Call for Immediate Estimate</span>
                </div>
              </div>
              <div className="cta-actions">
                <a href="mailto:info@shinytouchpainting.ca?subject=">
                  <div className="btn btn-primary btn-xl">
                    <span>Email Us for a Quote</span>
                  </div>
                </a>
                <button
                  id="openContactModal"
                  className="btn btn-outline btn-xl"
                >
                  Quick Contact Form
                </button>
              </div>
            </div>
          </div>
        </div>
        <dialog id="contactModal">
          <div className="modal-inner">
            <h3 className="section-subtitle">Get Your Free Estimate</h3>
            <form
              data-form-id="0d1e0327-e9e5-4dc8-ae47-c1d72c07b4cb"
              className="services-quote-form"
              onSubmit={handleServicesModalSubmit}
            >
              <div className="services-form-group form-group">
                <label htmlFor="services-modal-name">Name</label>
                <input
                  type="text"
                  id="services-modal-name"
                  name="name"
                  required
                  placeholder="Your full name"
                  data-form-field-id="name"
                />
              </div>
              <div className="services-form-group form-group">
                <label htmlFor="services-modal-phone">Phone</label>
                <input
                  type="tel"
                  id="services-modal-phone"
                  name="phone"
                  required
                  placeholder="(604) 000-0000"
                  data-form-field-id="phone"
                />
              </div>
              <div className="services-form-group form-group">
                <label htmlFor="services-modal-email">Email</label>
                <input
                  type="email"
                  id="services-modal-email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  data-form-field-id="email"
                />
              </div>
              <div className="services-form-group form-group">
                <label htmlFor="services-modal-service">Service Needed</label>
                <select
                  id="services-modal-service"
                  name="service"
                  required
                  defaultValue=""
                  data-form-field-id="service"
                >
                  <option value="" disabled>
                    Select a service…
                  </option>
                  <option value="interior-painting">Interior Painting</option>
                  <option value="exterior-painting">Exterior Painting</option>
                  <option value="full-renovation">Full Renovation</option>
                  <option value="strata-maintenance">Strata Maintenance</option>
                </select>
              </div>
              <div className="services-form-group form-group">
                <label htmlFor="services-modal-message">Project Details</label>
                <textarea
                  id="services-modal-message"
                  name="message"
                  required
                  minLength={10}
                  placeholder="Tell us about your project..."
                  data-form-field-id="message"
                ></textarea>
              </div>
              {modalMail.message ? (
                <p
                  className="section-content"
                  style={{
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem',
                    color:
                      modalMail.status === 'ok'
                        ? 'var(--color-primary)'
                        : 'var(--color-accent)',
                  }}
                  role="status"
                >
                  {modalMail.message}
                </p>
              ) : null}
              <div className="form-footer">
                <button
                  type="submit"
                  id="services-modal-submit"
                  name="button"
                  data-form-field-id="services-modal-submit"
                  className="btn btn-primary"
                  disabled={modalMail.status === 'sending'}
                >
                  {modalMail.status === 'sending' ? 'Sending…' : 'Send Request'}
                </button>
                <button
                  type="button"
                  id="closeContactModal"
                  name="button"
                  data-form-field-id="closeContactModal"
                  className="btn btn-link"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </section>
      <div className="services-container3">
        <div className="services-container4">
          <InlineScript
            html={`<style>
dialog {
  border: none;
  border-radius: var(--border-radius-lg);
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
dialog::backdrop {
  background: var(--color-scrim);
  backdrop-filter: blur(4px);
}
</style>`}
          ></InlineScript>
        </div>
      </div>
      <div className="services-container5">
        <div className="services-container6">
          <InlineScript
            html={`<script defer data-name="shiny-touch-interactions">
(function(){
  const modal = document.getElementById("contactModal")
  const openBtn = document.getElementById("openContactModal")
  const closeBtn = document.getElementById("closeContactModal")

  if (openBtn && modal) {
    openBtn.addEventListener("click", () => {
      modal.showModal()
    })
  }

  if (closeBtn && modal) {
    closeBtn.addEventListener("click", () => {
      modal.close()
    })
  }

  if (modal) {
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        modal.close()
      }
    })
  }

  const timeline = document.querySelector(".timeline-wrapper")
  let isDown = false
  let startX
  let scrollLeft

  if (timeline) {
    timeline.addEventListener("mousedown", (e) => {
      isDown = true
      timeline.classList.add("active")
      startX = e.pageX - timeline.offsetLeft
      scrollLeft = timeline.scrollLeft
    })

    timeline.addEventListener("mouseleave", () => {
      isDown = false
      timeline.classList.remove("active")
    })

    timeline.addEventListener("mouseup", () => {
      isDown = false
      timeline.classList.remove("active")
    })

    timeline.addEventListener("mousemove", (e) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - timeline.offsetLeft
      const walk = (x - startX) * 2
      timeline.scrollLeft = scrollLeft - walk
    })
  }

  const revealElements = document.querySelectorAll(".service-card, .feature-card, .pricing-card")
  const revealOnScroll = () => {
    revealElements.forEach((el) => {
      const rect = el.getBoundingClientRect()
      const isVisible = rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 0.85
      if (isVisible) {
        el.style.opacity = "1"
        el.style.transform = "translateY(0)"
      }
    })
  }

  revealElements.forEach((el) => {
    el.style.opacity = "0"
    el.style.transform = "translateY(30px)"
    el.style.transition = "all 0.6s ease-out"
  })

  window.addEventListener("scroll", revealOnScroll)
  revealOnScroll()
})()
</script>`}
          ></InlineScript>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
}
